import type { DesignDocument } from '@forgd/contract/types'
import type { DraftRejection } from '#design-tokenomics/utils/draft'
import {
  createDraft,
  DRAFT_STORAGE_KEY,
  parseDraft,
  serializeDraft,
} from '#design-tokenomics/utils/draft'
import { createLogger } from '#logger'
import { useStorage } from '@vueuse/core'

const logger = createLogger('design-tokenomics/draft')

/**
 * The anonymous visitor's unsaved work: the whole design, not just its numbers.
 *
 * Kept as a raw string so validation stays explicit on every read: a draft can outlive a deploy,
 * and a document that no longer matches the schema must fall back to the starting design rather
 * than render values nobody chose.
 */
export function useDesignDraft() {
  const raw = useStorage<string | null>(DRAFT_STORAGE_KEY, null)

  const document = computed<DesignDocument | null>(() => {
    const result = parseDraft(raw.value, Date.now())

    if (result.ok) {
      return result.draft.document
    }

    if (result.reason !== 'empty') {
      logger.debug('discarding stored draft', { reason: result.reason })
    }

    return null
  })

  const updatedAt = computed<string | null>(() => {
    const result = parseDraft(raw.value, Date.now())
    return result.ok ? result.draft.updatedAt : null
  })

  const hasDraft = computed(() => document.value !== null)

  function save(next: DesignDocument): { ok: true } | { ok: false, reason: DraftRejection } {
    const written = serializeDraft(createDraft(next, Date.now()))

    if (!written.ok) {
      // Losing the write is better than corrupting storage other features share.
      logger.warn('refusing to store draft', { reason: written.reason })
      return written
    }

    raw.value = written.raw
    return { ok: true }
  }

  function clear() {
    raw.value = null
  }

  /**
   * Drops a draft that failed validation so it cannot be re-parsed on every render. Safe to call
   * eagerly: a valid draft is left alone.
   */
  function pruneIfUnusable() {
    if (raw.value !== null && document.value === null) {
      clear()
    }
  }

  return {
    document,
    updatedAt,
    hasDraft,
    save,
    clear,
    pruneIfUnusable,
  }
}
